import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { Play } from "lucide-react";

// Clip Twitch del canale (mie e degli spettatori): TUTTE, piu' recenti davanti,
// si aggiornano da sole (polling ogni minuto; il server ricarica da Twitch ogni 5 min).
export function ClipsSection() {
  const { t } = useLang();
  const [clips, setClips] = useState(null);
  const railRef = useRef(null);

  useEffect(() => {
    const load = () => api.get("/clips").then(({ data }) => setClips(data.clips || [])).catch(() => setClips((prev) => prev ?? []));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="py-24 border-t border-[#1A1A1A]" data-testid="clips-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("clips.over")}</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-4">{t("clips.title")}</h2>
          <p className="text-steel max-w-lg mb-14">{t("clips.text")}</p>
        </Reveal>
        {clips === null ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel">…</p>
        ) : clips.length === 0 ? (
          <div className="chamfer border border-[#1A1A1A] bg-[#050505] p-8" data-testid="clips-empty">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D51]">{t("clips.none")}</p>
          </div>
        ) : (
          <>
            {/* 3 clip a riga x 2 righe (6 visibili); oltre, la rotellina scorre in orizzontale
                (classe wheel-x: la converte il gestore globale; data-lenis-prevent esclude Lenis) */}
            <div
              ref={railRef}
              className="clips-rail wheel-x grid grid-rows-2 grid-flow-col gap-px bg-[#1A1A1A] border border-[#1A1A1A] overflow-x-auto overscroll-contain"
              data-lenis-prevent
              data-testid="clips-grid"
            >
              {clips.map((c, i) => {
                const isNew = c.created_at && (Date.now() - new Date(c.created_at).getTime()) < 48 * 3600 * 1000;
                return (
                  <Reveal key={c.id} delay={(i % 3) * 0.08} className="bg-[#050505] h-full">
                    <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`clip-card-${c.id}`} className="group block h-full">
                      <div className="relative aspect-video overflow-hidden">
                        <img src={c.thumbnail_url} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-[#050505]/0 group-hover:bg-[#050505]/40 transition-colors duration-300 flex items-center justify-center">
                          <Play className="w-10 h-10 text-wardogs opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" />
                        </div>
                        {isNew && (
                          <span className="absolute top-2 left-2 bg-wardogs text-[#050505] px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em]">Nuova</span>
                        )}
                        <span className="absolute bottom-2 right-2 bg-[#050505]/85 px-2 py-0.5 font-mono text-[10px] text-white">{Math.round(c.duration)}s</span>
                      </div>
                      <div className="p-4">
                        <p className="font-display text-lg uppercase leading-tight truncate group-hover:text-wardogs transition-colors duration-300">{c.title}</p>
                        <p className="font-mono text-[10px] text-steel mt-1">
                          {t("clips.by", { name: c.creator })} · {t("clips.views", { n: c.views })}
                        </p>
                      </div>
                    </a>
                  </Reveal>
                );
              })}
            </div>
            {clips.length > 6 && (
              <div className="mt-3 flex items-center justify-between" data-testid="clips-scrollhint">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4A4D51]">rotellina per scorrere le clip →</span>
                <span className="font-mono text-[10px] text-steel">+{clips.length - 6} altre clip</span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
